/*
 * window.VoyadoClient — Elevate Storefront (v3) query + notification client.
 * Builds its own instance of Voyado's Elevate SDK (@apptus/esales-api, pinned in
 * layout/theme.liquid), configured with the theme's identity() so consent is
 * resolved per request, and exposes that instance as window.elevate.
 * API: autocomplete/searchPage/landingPage/productPage/cartPage queries +
 * notifyAddToCart/notifyAddFavorite/ensureCartStamped seams, attachSdk (the
 * module script's entry point) and unavailableReason.
 * Gotcha: queries are gated on OneTrust consent (Functional group C0003); ANONYMOUS when denied.
 */
;(function () {
  'use strict';

  const URL_PARAMS = new URL(window.location.href).searchParams;
  const PREVIEW = URL_PARAMS.get('PREVIEW') === 'true';
  const SESSION_KEY = 'brora.voyado.session';

  function cfg() { return window.broraVoyado || {}; }
  // Cluster host from the theme's Cluster ID setting, server-rendered into window.broraVoyado so it is
  // present at first paint (D82). Passed whole — clusterId takes a full URL.
  function clusterHost() {
    const u = cfg().clusterUrl;
    return (typeof u === 'string' && u) ? u.replace(/\/+$/, '') : null;
  }
  // Elevate defines only DESKTOP/MOBILE and prescribes no breakpoint, so follow the layout the visitor
  // sees: --breakpoint-lg, the same token the section scripts read as DESKTOP_BREAKPOINT.
  const DESKTOP_BREAKPOINT = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--breakpoint-lg'), 10) || 1024;
  function touchpoint() { return window.innerWidth < DESKTOP_BREAKPOINT ? 'MOBILE' : 'DESKTOP'; }

  // Consent gate — Voyado is CRM personalisation, so gate on OneTrust's Functional group C0003 (deny by default).
  // If the OneTrust admin re-maps categories, update CONSENT_GROUP (Voyado cookie not yet categorised there — #137).
  const CONSENT_GROUP = 'C0003';

  // Accepted groups as ",C0001,C0003,". Read the OptanonConsent cookie first — it's the authoritative
  // synchronous store of consent, so a returning consenter keys correctly before the OnetrustActiveGroups
  // global settles (which can seed an incomplete placeholder). Fall back to the global (refreshed on
  // OneTrustGroupsUpdated); null when neither is available.
  function oneTrustGroups() {
    try {
      const m = document.cookie.match(/(?:^|;\s*)OptanonConsent=([^;]+)/);
      if (m) {
        const raw = (decodeURIComponent(m[1]).match(/(?:^|&)groups=([^&]*)/) || [])[1] || '';
        const accepted = raw.split(',').filter(function (p) { return /:1$/.test(p); })
          .map(function (p) { return p.split(':')[0]; });
        if (accepted.length) return ',' + accepted.join(',') + ',';
      }
    } catch (e) {}
    const g = window.OnetrustActiveGroups || window.OptanonActiveGroups;
    if (typeof g === 'string' && g) return g;
    return null;
  }

  function trackingAllowed() {
    const groups = oneTrustGroups();
    return groups !== null && groups.indexOf(',' + CONSENT_GROUP + ',') !== -1; // null (unresolved) → deny
  }
  function randomKey() {
    try { return crypto.randomUUID(); }
    catch (e) { return 'anon-' + Math.random().toString(36).slice(2) + Date.now().toString(36); }
  }

  // Reads the stored identity without creating one. Storing an identifier on the device needs consent
  // in its own right, whether or not it is ever sent, so the denial path reads through this.
  function readSession() {
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      const s = raw ? JSON.parse(raw) : null;
      if (s && s.customerKey && s.sessionKey) return s;
    } catch (e) { /* private mode / malformed */ }
    return null;
  }

  function clearSession() {
    try { localStorage.removeItem(SESSION_KEY); } catch (e) { /* private mode */ }
  }

  // Persistent identity — a stable random customerKey/sessionKey in our own
  // storage (not the Accelerator's voyado.session), reused across page loads.
  function persistentSession() {
    const stored = readSession();
    if (stored) return stored;
    const fresh = { customerKey: randomKey(), sessionKey: randomKey() };
    try { localStorage.setItem(SESSION_KEY, JSON.stringify(fresh)); } catch (e) { /* quota / private mode */ }
    return fresh;
  }
  // No consent → ANONYMOUS user context with both keys omitted (sending keys alongside userContext is
  // an error). Resolved on every query and notification, so consent is read per request.
  function identity() {
    if (!trackingAllowed()) return { userContext: 'ANONYMOUS' };
    const s = persistentSession();
    return { customerKey: s.customerKey, sessionKey: s.sessionKey };
  }

  // Queries reach fetch as a URL object. Wrapped's fetch wrapper calls input.match() on it without
  // String(), and its catch then writes to localStorage unguarded, so the request dies wherever storage
  // is blocked — hand that wrapper a string instead. Only URLs aimed at the Elevate cluster are rewritten.
  // Re-asserted before each dispatch to stay outermost of the app wrappers; the marker makes that a
  // no-op once it is.
  function ensureUrlShim() {
    const inner = window.fetch;
    if (typeof inner !== 'function' || inner.__broraUrlShim) return;
    const host = clusterHost();
    const shim = function (input, init) {
      const target = (host && input instanceof URL && input.href.indexOf(host) === 0) ? input.href : input;
      // A bare fetch(url) leaves `this` undefined under strict mode, and native fetch rejects any
      // receiver but the global.
      return inner.call(this || window, target, init);
    };
    shim.__broraUrlShim = true;
    window.fetch = shim;
  }

  // A failed landing query renders as the PLP's empty state, so an unreachable Elevate otherwise reads
  // as "no products". Recorded for the console, for CSS, and for any listener.
  let unavailableReason = null;
  function markUnavailable(reason, detail) {
    if (unavailableReason) return;
    unavailableReason = reason;
    console.warn('[voyado] Elevate unavailable — every Voyado surface will be empty:', reason, detail || '');
    try { document.documentElement.setAttribute('data-voyado-unavailable', reason); } catch (e) {}
    try { document.dispatchEvent(new CustomEvent('voyado:unavailable', { detail: { reason: reason, detail: detail } })); } catch (e) {}
  }

  // Module URL from window.broraVoyado.esalesModuleUrl — the pinned string layout/theme.liquid also
  // modulepreloads, so the preload and this import share one fetch. A rejection is never cached — one
  // transient failure would leave every Voyado surface dead until a reload. Retries to the ceiling,
  // then Elevate counts as unavailable.
  const MAX_LOAD_ATTEMPTS = 3;
  let apiPromise = null, loadAttempts = 0, elevateApi = null;
  function loadFailed(reason, detail) {
    if (loadAttempts >= MAX_LOAD_ATTEMPTS) markUnavailable(reason, detail);
  }

  // Builds the single instance from a loaded module and publishes it as window.elevate, the global
  // Voyado's integration guidance expects, unless the app embed (where still enabled) has published its
  // own. It resolves identity through identity(), so the consent gate applies. A module that lands after
  // the load timeout clears that signal. Throws when Elevate rejects the config.
  function buildApi(mod) {
    if (elevateApi) return elevateApi;
    const c = cfg();
    elevateApi = mod.elevate({
      clusterId: clusterHost(), // full URL — the SDK parses it as-is
      market: c.market,
      locale: c.locale,
      touchpoint: touchpoint().toLowerCase(), // the config takes lowercase; params carry the live value
      session: identity
    });
    if (!window.elevate) window.elevate = elevateApi;
    if (unavailableReason === 'module-timeout') {
      unavailableReason = null;
      try { document.documentElement.removeAttribute('data-voyado-unavailable'); } catch (e) {}
    }
    return elevateApi;
  }

  // Entry point for the async module script in layout/theme.liquid (directly, or through the module it
  // leaves on window.broraVoyadoSdk when it runs before this file), so the instance is built as soon as
  // the SDK lands — before DOMContentLoaded in the normal case. Load failures are left to loadApi().
  function attachSdk(mod) {
    const c = cfg();
    if (elevateApi || !mod || typeof mod.elevate !== 'function' || !clusterHost() || !c.market || !c.locale) return;
    try { buildApi(mod); } catch (e) { /* reported by loadApi() as config-rejected */ }
  }

  function loadApi() {
    if (elevateApi) return Promise.resolve(elevateApi);
    if (apiPromise) return apiPromise;
    if (loadAttempts >= MAX_LOAD_ATTEMPTS) return Promise.reject(new Error('voyado_no_module'));
    loadAttempts++;
    const attempt = new Promise(function (resolve, reject) {
      const host = clusterHost();
      const c = cfg();
      if (!host || !c.market || !c.locale) {
        loadFailed('config', { clusterUrl: host, market: c.market, locale: c.locale });
        reject(new Error('voyado_missing')); return;
      }
      let moduleUrl;
      try {
        const u = new URL(c.esalesModuleUrl);
        // Limits the import to one exact version of Voyado's published package on unpkg. The bytes are
        // pinned separately, by the integrity hash in layout/theme.liquid's import map.
        if (u.protocol !== 'https:' || u.hostname !== 'unpkg.com' || !/^\/@apptus\/esales-api@\d+\.\d+\.\d+\/dist\/mod\.mjs$/.test(u.pathname)) {
          loadFailed('module-origin', u.href); reject(new Error('voyado_no_module')); return;
        }
        moduleUrl = u.href;
      } catch (e) { loadFailed('module-url', String(e && e.message || e)); reject(new Error('voyado_no_module')); return; }
      // A module fetch that never answers would leave every Voyado surface loading indefinitely, so after
      // the same 8s the queries allow Elevate counts as unavailable. A module that lands later still
      // builds the instance (see buildApi), and later queries pick it up through elevateApi.
      const timer = setTimeout(function () {
        if (elevateApi) { resolve(elevateApi); return; } // built meanwhile through attachSdk()
        loadAttempts = MAX_LOAD_ATTEMPTS;
        markUnavailable('module-timeout', moduleUrl);
        reject(new Error('voyado_no_module'));
      }, 8000);
      import(moduleUrl).then(function (mod) {
        clearTimeout(timer);
        if (!mod || typeof mod.elevate !== 'function') {
          loadFailed('module-shape', 'elevate.js loaded but exports no elevate()');
          reject(new Error('voyado_no_module')); return;
        }
        // The constructor throws on a config it rejects, so it belongs inside the guarded path.
        try {
          resolve(buildApi(mod));
        } catch (e) {
          loadFailed('config-rejected', String(e && e.message || e));
          reject(new Error('voyado_missing'));
        }
      }, function (e) {
        clearTimeout(timer);
        loadFailed('module-load-failed', String(e && e.message || e) + ' — ' + moduleUrl);
        reject(new Error('voyado_no_module'));
      });
    });
    // Released once settled, and only while it is still the current attempt, so callers in the same
    // tick share one load.
    apiPromise = attempt;
    attempt.catch(function () { if (apiPromise === attempt) apiPromise = null; });
    return attempt;
  }

  // Per-request parameters only — the instance config carries market and locale, the session carries
  // the identity. touchpoint is overridden here because the config is fixed at build time while this
  // reads the layout at call time, so a rotate or resize is reflected.
  function mergeParams(params) {
    const merged = Object.assign({ touchpoint: touchpoint() }, params || {});
    if (identity().userContext) {
      // `notify` cannot be combined with any userContext (Elevate 400). A live-typing preview becomes
      // SYNTHETIC, the sanctioned notify=false replacement — results returned, no metrics collected —
      // so a partial term is never registered. A committed query keeps the session's ANONYMOUS.
      if (merged.notify === 'false') merged.userContext = 'SYNTHETIC';
      delete merged.notify;
    }
    if (PREVIEW) merged.viewId = 'preview';
    return merged;
  }

  // 8s timeout. No per-request abort is available, so the request runs on and only the promise gives
  // up. Surfaces as 'voyado_timeout', the error the callers already handle.
  function withTimeout(p) {
    let timer;
    return Promise.race([
      p,
      new Promise(function (_, reject) { timer = setTimeout(function () { reject(new Error('voyado_timeout')); }, 8000); })
    ]).finally(function () { clearTimeout(timer); });
  }

  function runQuery(name, params, body) {
    const merged = mergeParams(params);
    // autocomplete is always POST, which a truthy body selects.
    const payload = (name === 'autocomplete') ? (body || {}) : body;
    return loadApi().then(function (api) {
      ensureUrlShim();
      return withTimeout(api.query[name](merged, payload));
    });
  }

  const NOTIFY_METHOD = { 'click': 'click', 'add-to-cart': 'addToCart', 'add-favorite': 'addFavorite', 'remove-favorite': 'removeFavorite' };

  // Low-level notification — consent-gated and fire-and-forget.
  function postNotification(path, value) {
    if (!trackingAllowed()) return; // suppressed entirely, never sent anonymously
    loadApi().then(function (api) {
      ensureUrlShim();
      // Notifications reject on a non-2xx; a failed one must never reach the console.
      return api.notify[NOTIFY_METHOD[path]](value);
    }).catch(function () {});
  }

  // Ticket-based notifications (click, add-to-cart) — ticket comes from a query result.
  function notify(path, ticket) {
    if (!ticket) return;
    postNotification(path, ticket);
  }

  // Favourite notifications — keyed by productKey (= Shopify product id), no ticket.
  function notifyFavorite(path, productKey) {
    if (productKey == null || productKey === '') return;
    postNotification(path, String(productKey));
  }

  // Cart-attribute bridge — Voyado attributes a purchase from these order attributes, so stamp the
  // Elevate identity onto the cart (attribute names are fixed by Voyado).
  const CART_ATTR_CUSTOMER = '_elevate_customer_key';
  const CART_ATTR_SESSION = '_elevate_session_key';
  const CART_ATTR_MARKET = '_elevate_market';
  // Locale-aware cart root (multi-market store); falls back to '/'.
  function cartRoot() {
    const r = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return typeof r === 'string' && r ? r : '/';
  }
  let cartStamping = false; // drop overlapping passes
  function ensureCartStamped() {
    // Consent gate — the two identity keys are stamped only with consent and cleared on deny/withdrawal.
    // The market is not personal data and Voyado's anonymous order path still reads it, so a denied
    // cart with items keeps it.
    const id = identity();
    const denied = (id.userContext === 'ANONYMOUS');
    const market = cfg().market;
    if (!denied && (!id.customerKey || !id.sessionKey || !market)) return;
    if (cartStamping) return;
    cartStamping = true;
    const root = cartRoot();
    fetch(root + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (r) { if (!r.ok) throw new Error('cart_http_' + r.status); return r.json(); })
      .then(function (cart) {
        const a = (cart && cart.attributes) || {};
        if (denied) {
          const keysStamped = a[CART_ATTR_CUSTOMER] || a[CART_ATTR_SESSION];
          const marketMissing = !!(market && cart && cart.item_count && a[CART_ATTR_MARKET] !== market);
          if (!keysStamped && !marketMissing) return null;
          const cleared = {};
          cleared[CART_ATTR_CUSTOMER] = '';
          cleared[CART_ATTR_SESSION] = '';
          cleared[CART_ATTR_MARKET] = (market && cart && cart.item_count) ? market : '';
          return fetch(root + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: cleared }),
            keepalive: true
          });
        }
        if (!cart || !cart.item_count) return null;
        if (a[CART_ATTR_CUSTOMER] === id.customerKey && a[CART_ATTR_SESSION] === id.sessionKey && a[CART_ATTR_MARKET] === market) {
          return null;
        }
        const attributes = {};
        attributes[CART_ATTR_CUSTOMER] = id.customerKey;
        attributes[CART_ATTR_SESSION] = id.sessionKey;
        attributes[CART_ATTR_MARKET] = market;
        return fetch(root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: attributes }),
          keepalive: true
        });
      })
      .catch(function () {})
      .finally(function () { cartStamping = false; });
  }

  // Where the Voyado Elevate app embed is still enabled, it writes these three attributes under its own
  // gate, which asks about Shopify analytics (OneTrust Performance) where this one asks Functional, so
  // it can clear this stamp. Attribution is read from the order, so re-stamp on the way to checkout.
  // A single keepalive write, with no read-first pass: only one request is likely to survive unload.
  function stampForCheckout() {
    const id = identity();
    if (id.userContext === 'ANONYMOUS') return; // a denied cart is never stamped with identity keys
    const market = cfg().market;
    if (!id.customerKey || !id.sessionKey || !market) return;
    const attributes = {};
    attributes[CART_ATTR_CUSTOMER] = id.customerKey;
    attributes[CART_ATTR_SESSION] = id.sessionKey;
    attributes[CART_ATTR_MARKET] = market;
    try {
      fetch(cartRoot() + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attributes }),
        keepalive: true
      }).catch(function () {});
    } catch (e) {}
  }
  // mousedown beats navigation, but the entry point is a link and a keyboard Enter raises click without
  // mousedown, so both are bound. The write is idempotent and the guard skips the second.
  let checkoutStamped = false;
  function onCheckoutIntent(e) {
    const t = e.target;
    if (!t || typeof t.closest !== 'function') return; // a non-Element target has no closest()
    if (!t.closest('a[href*="/checkout"]')) return;
    if (checkoutStamped) return;
    checkoutStamped = true;
    setTimeout(function () { checkoutStamped = false; }, 2000); // a cancelled navigation can retry
    stampForCheckout();
  }
  document.addEventListener('mousedown', onCheckoutIntent);
  document.addEventListener('click', onCheckoutIntent);

  window.VoyadoClient = {
    autocomplete: function (params, body) { return runQuery('autocomplete', params, body); },
    searchPage:   function (params, body) { return runQuery('searchPage', params, body); },
    landingPage:  function (params, body) { return runQuery('landingPage', params, body); },
    productPage:  function (params, body) { return runQuery('productPage', params, body); },
    cartPage:     function (params, body) { return runQuery('cartPage', params, body); },
    // Imperative add-to-cart notify for surfaces that can't use the cart:added seam
    // (e.g. the PDP sticky bar). Inherits notify()'s consent + empty-ticket guards.
    notifyAddToCart: function (ticket) { notify('add-to-cart', ticket); },
    // Favourites — by productKey, wired from the wishlist seam (wishlist-client.js).
    notifyAddFavorite: function (productKey) { notifyFavorite('add-favorite', productKey); },
    notifyRemoveFavorite: function (productKey) { notifyFavorite('remove-favorite', productKey); },
    ensureCartStamped: function () { ensureCartStamped(); },
    attachSdk: attachSdk,
    // Null while healthy; the reason string once Elevate has been found unreachable.
    unavailableReason: function () { return unavailableReason; }
  };

  // Document-level notify wiring. Click fires on mousedown (not click) so it beats
  // navigation and can't race the page unload; ATC from the cart:added event.
  document.addEventListener('mousedown', function (e) {
    const el = e.target.closest('[data-voyado-ticket]');
    if (el) {
      const t = el.getAttribute('data-voyado-ticket');
      if (t) notify('click', t);
    }
  });
  document.addEventListener('cart:added', function (e) {
    const trigger = e.detail && e.detail.trigger;
    const t = trigger && trigger.dataset && trigger.dataset.voyadoTicket;
    if (t) notify('add-to-cart', t);
    ensureCartStamped();
  });

  // On login, set customerKey to the recovered Elevate guest key (window.broraVoyado.elevateKey → customer.id
  // fallback) so pre-login history links to the account via Elevate's same-session Linked keys; logout → fresh
  // anon. See docs/orchestrations/orch_VOYADO_GUEST_KEY_RECOVERY.md. The pre-login key is mapped onto the
  // customer's Voyado contact (customer-sync) just before the switch.
  function writeSession(s) {
    try { localStorage.setItem(SESSION_KEY, JSON.stringify(s)); } catch (e) { /* quota / private mode */ }
  }

  // Maps an Elevate key onto the customer's Voyado contact through the Elevate app's proxy. Called once,
  // on the login that links the key, and not retried. ctrlStr is the base64 customer email the app
  // expects (layout/theme.liquid).
  const CUSTOMER_SYNC_URL = '/apps/voyado-elevate/customer-sync';
  function syncCustomer(customerKey) {
    const ctrlStr = cfg().ctrlStr;
    if (!customerKey || !ctrlStr) return;
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller ? setTimeout(function () { controller.abort(); }, 8000) : null;
    fetch(CUSTOMER_SYNC_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ elevateCustomerKey: customerKey, ctrlStr: ctrlStr }),
      keepalive: true,
      signal: controller ? controller.signal : undefined
    }).catch(function () {}).finally(function () { if (timer) clearTimeout(timer); });
  }

  function syncCustomerIdentity() {
    if (!window.broraVoyado) return;
    if (!trackingAllowed()) {
      // Consent denied/withdrawn — the keys are non-essential storage, so this path leaves nothing
      // of ours on the device. persistentSession() mints a fresh pair once consent is given.
      if (readSession()) clearSession();
      return;
    }
    const cid = window.broraVoyado.customerId;
    const priorKey = window.broraVoyado.elevateKey; // Accelerator-captured guest key (app--… / custom.* fallback)
    const stored = readSession(); // read before persistentSession() can mint a pair
    const s = persistentSession();
    if (cid != null && cid !== '') {
      const key = String(priorKey || cid); // recovered guest key wins; customer.id is the fallback
      if (s.customerKey !== key) {
        // Only a guest key with history is mapped: never an already-linked key, the customer id, or a
        // pair minted on this load.
        if (stored && !stored.linked && stored.customerKey !== String(cid)) syncCustomer(stored.customerKey);
        writeSession({ customerKey: key, sessionKey: s.sessionKey, linked: true });
        ensureCartStamped();
      }
    } else if (s.linked) {
      const fresh = { customerKey: randomKey(), sessionKey: randomKey() }; // logged out → fresh anonymous
      writeSession(fresh);
      ensureCartStamped();
    }
  }

  // Re-run identity + cart-stamp on OneTrustGroupsUpdated (fires on grant AND withdrawal) + once on init;
  // trackingAllowed() re-reads OneTrust, and both fns are idempotent.
  function onConsentChange() {
    syncCustomerIdentity();
    ensureCartStamped();
  }
  window.addEventListener('OneTrustGroupsUpdated', onConsentChange);
  onConsentChange(); // initial run

  // Started at init so the modulepreloaded module is consumed as soon as it lands, whether or not the
  // async module script in layout/theme.liquid has run. The instance stores nothing on the device:
  // identity comes from identity(), which is consent-gated.
  if (window.broraVoyadoSdk) attachSdk(window.broraVoyadoSdk);
  loadApi().catch(function () {});
})();
